import { useState } from 'react'
import { mergeAttributes } from '@tiptap/core'
import Image from '@tiptap/extension-image'
import {
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type NodeViewProps,
} from '@tiptap/react'
import { X } from '@/components/icons'

import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { cn } from '@/lib/utils'

/**
 * Image with an optional caption (사진 설명).
 *
 * Keeps the node name `image` (so the server-derived thumbnail still finds it via
 * `type === 'image'`) and the permissive body schema, but adds a `caption`
 * string attribute. In the EDITOR a React NodeView renders the image with an
 * inline-editable caption field below it; in the READ view generateHTML emits
 * `<figure><img><figcaption>` (figcaption only when a caption is present).
 * Existing caption-less images keep working.
 *
 * In editable mode a top-right (×) button removes the image — clicking it opens a
 * confirm dialog ("이미지를 삭제할까요?") before deleting the node. Shared by the
 * survey and article editors (identical insert/caption/remove logic).
 */
function ImageCaptionView({
  node,
  updateAttributes,
  deleteNode,
  selected,
  editor,
}: NodeViewProps) {
  const caption = (node.attrs.caption as string | null) ?? ''
  const [confirmOpen, setConfirmOpen] = useState(false)
  return (
    <NodeViewWrapper as="figure" className="image-figure">
      <div className="relative">
        <img
          src={node.attrs.src as string}
          alt={(node.attrs.alt as string | null) ?? ''}
          draggable={false}
          className={cn('rounded-lg', selected && 'ring-2 ring-primary')}
        />
        {editor.isEditable && (
          <button
            type="button"
            aria-label="이미지 삭제"
            title="이미지 삭제"
            // Keep the click on the button — don't let ProseMirror select/move the node.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setConfirmOpen(true)}
            className="absolute right-2 top-2 flex size-8 items-center justify-center rounded-full bg-black/60 text-white opacity-90 transition-opacity hover:bg-black/80 hover:opacity-100"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {/*
        **읽기 화면과 같은 상자.** 설명이 없으면 읽기 화면에 <figcaption> 이 없으므로 편집기도 안 그린다 —
        항상 그리면 편집기의 그림이 읽기 화면보다 32px 길어져 그 아래 배치가 어긋난다(실측 2026-09-18).
        이미지를 고르면(selected) 입력 칸이 나타나 설명을 넣을 수 있다. 설명이 있으면 <figcaption> 안의
        입력 칸이 읽기 화면의 <figcaption> 과 같은 글자 크기·줄 높이·여백으로 선다.
      */}
      {editor.isEditable && (selected || caption !== '') ? (
        <figcaption>
          <input
            type="text"
            value={caption}
            placeholder="사진 설명을 입력하세요 (선택)"
            aria-label="사진 설명"
            onChange={(e) => updateAttributes({ caption: e.target.value })}
            // Keep keystrokes/clicks inside the input — don't let ProseMirror handle them.
            onKeyDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ font: 'inherit', lineHeight: 'inherit', color: 'inherit' }}
            className="w-full border-0 bg-transparent p-0 text-center outline-none placeholder:text-text-subtle"
          />
        </figcaption>
      ) : null}
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false)
          deleteNode()
        }}
        title="이미지를 삭제할까요?"
        description="이 이미지를 본문에서 제거합니다."
        confirmLabel="삭제"
        destructive
      />
    </NodeViewWrapper>
  )
}

export const ImageWithCaption = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      caption: {
        default: '',
        // Rendered as <figcaption> (below), never as an <img> attribute.
        renderHTML: () => ({}),
      },
    }
  },

  renderHTML({ node, HTMLAttributes }) {
    const caption = String(node.attrs.caption ?? '').trim()
    const img = ['img', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)]
    return caption
      ? ['figure', { class: 'image-figure' }, img, ['figcaption', {}, caption]]
      : ['figure', { class: 'image-figure' }, img]
  },

  parseHTML() {
    return [
      {
        tag: 'figure',
        getAttrs: (el) => {
          const figure = el as HTMLElement
          const img = figure.querySelector('img')
          if (!img) return false
          return {
            src: img.getAttribute('src'),
            alt: img.getAttribute('alt'),
            caption: figure.querySelector('figcaption')?.textContent ?? '',
          }
        },
      },
      { tag: 'img[src]' },
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageCaptionView)
  },
})
